import { EDecision, EPaymentFrequency } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { useMutation, useQuery } from '@tanstack/react-query'

/** Mirrors the backend's ProtectionPlanCoverageEnum; the worksheet models it as two booleans. */
export const EProtectionPlanCoverage = {
  None: 'none',
  Single: 'single',
  Joint: 'joint',
} as const

export type EProtectionPlanCoverage = (typeof EProtectionPlanCoverage)[keyof typeof EProtectionPlanCoverage]

/** The ERS is sent directly, so the backend's norms analyzer resolves the rate. */
export interface CalculatorEstimateRequestDto {
  merchantId: string
  /** Ignored by programs that do not price on the ERS. */
  ersScore?: number | null
  /** The borrower's province — rate caps and fees follow it. */
  provinceIso: string
  amountRequested: number
  /** Omit to use the longest term the prequalification allows. */
  term?: number | null
  paymentPlanId: string
  paymentFrequency: EPaymentFrequency
  /** Joint prices the protection plan at the with-co-applicant rate. */
  protectionPlan: EProtectionPlanCoverage
  deliveryOn?: string | null
  firstPaymentOn?: string | null
}

export interface CalculatorEstimateResultDto {
  interestRate: number
  lenderFeeRate: number
  lenderFee: number
  merchantFeeRate: number
  merchantFee: number
  protectionPlanAmount: number
  totalInterestAmount: number
  paymentForFrequency: number
  effectiveRate: number
  term: number
  decision: EDecision
  maxLoanAmount: number
  maxLoanTerm: number
  refusalReasonCodes: string[]
  warningCodes: string[]
}

const calculatorEstimate = async ({
  financingProgramId,
  request,
}: {
  financingProgramId: string
  request: CalculatorEstimateRequestDto
}): Promise<CalculatorEstimateResultDto> => {
  const response = await getApiClient().post<CalculatorEstimateResultDto>(
    `/Applications/${financingProgramId}/Calculator/Estimate`,
    request,
  )
  return response.data
}

export const useCalculatorEstimate = () =>
  useMutation({
    mutationFn: calculatorEstimate,
  })

const calculatorFirstPaymentOptions = async ({
  financingProgramId,
  deliveryOn,
  paymentFrequency,
}: {
  financingProgramId: string
  deliveryOn: string
  paymentFrequency: string
}): Promise<string[]> => {
  const response = await getApiClient().post<string[]>(
    `/Applications/${financingProgramId}/Calculator/FirstPaymentOptions`,
    { deliveryOn, paymentFrequency },
  )
  return response.data
}

/**
 * The first payment dates the program's rules allow. Same rule source as the worksheet's own
 * options endpoint, without needing a credit application. Pass `null` to disable.
 */
export const useCalculatorFirstPaymentOptions = (
  args: { financingProgramId: string; deliveryOn: string; paymentFrequency: string } | null,
): [string[], boolean] => {
  const { data, isFetching } = useQuery({
    queryKey: ['calculatorFirstPaymentOptions', args],
    queryFn: () => calculatorFirstPaymentOptions(args!),
    enabled: args !== null,
  })

  return [data ?? [], isFetching]
}
